import * as MaterialUiIcons from "@mui/icons-material";
import * as React from "react";
import * as ReactDnd from "react-dnd";
import { alertErrorForegroundRed, alertWarningForegroundAmber } from "Styling/Palette/AlertColours";
import { FunctionComponent } from "react";
import { HTML5Backend } from "react-dnd-html5-backend";
import CreateSensorGroupModalPanel from "Panels/Composite/Modals/CreateSensorGroupModalPanel";
import DeleteIconButton from "Components/Buttons/Composite/DeleteIconButton";
import EditSensorGroupModalPanel from "Panels/Composite/Modals/EditSensorGroupModalPanel";
import Sensor from "Model/Sensor";
import SensorGroup from "Model/SensorGroup";
import Services from "Services/Platform/Services";
import { canDeleteSensorGroup, canEditSensorGroup } from "Utilities/EditPermissions";
import ServiceGeneralError from "Services/Platform/ServiceGeneralError";
import { AreaAwareSensorGroup } from "Services/Platform/SensorGroupsService";
import UploadSensorsModalPanel from "./Modals/UploadSensorsModalPanel";
import CloudUploadButton from "../../Components/Buttons/Composite/CloudUploadButton";
import { Alert, Button, FormControl, OutlinedInput, Snackbar, Tooltip } from "@mui/material";
import FirstIfNotEmpty from "../../Utilities/FirstIfNotEmpty";
import CorrosionRateText from "../../Components/Text/CorrosionRateText";
import DisplacementText from "../../Components/Text/DisplacementText";
import { Sensorgroup, Trendanalysis, Trendperiodtype } from "@inductosense/typescript-fetch";
import DropDownListControlled from "../../Components/Input/DropDownListControlled";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { NodeModel, Tree, TreeMethods } from "@minoru/react-dnd-treeview";
import SimplePanel from "../SimplePanel";
import { CSSProperties } from "@mui/material/styles/createMixins";
import merge from "../../Utilities/Merge";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "../../Components/Buttons/IconButton";
import useDebounce from "../../Components/Input/UseDebounce";
import SortButton from "../../Components/Buttons/SortButton";
import MesSvg from "Graphics/Icons/MesSvg.svg";
import SdlSvg from "Graphics/Icons/SdlSvg.svg"
import SensorSvg from "Graphics/Icons/SensorSvg.svg"
import { canCreateSensorGroup } from "../../Utilities/EditPermissions";


const sensorLabelCompactStyle: CSSProperties = {
    paddingRight: 10,
    flexShrink: 0,
    fontSize: "9pt"
};

const sensorLabelStyle: CSSProperties = {
    ...sensorLabelCompactStyle,
    fontSize: "11pt"
};

// This is a client-only parent used to place multiple database Area roots next
// to each other. It is never sent to the API and cannot become a SensorGroup Id.
const legacyAreasTreeRootId = "__idart1_legacy_areas_tree_root__";

interface SensorTreePanelProps {
    rootSensorGroup: SensorGroup;
    selectedNodes: string[];
    latestSensorTrends?: Trendanalysis[];
    onSensorCreateClick(creationAreaName?: string): void;
    onSensorEditClick(sensor: Sensor): void;
    onSensorsSelect(sensors: Sensor[]): void;
    onSensorGroupClick(sensorGroup: SensorGroup): void;
    onSensorTreeChange(): Promise<void>;
    tabletMode: boolean;
    unitsMode: "metric" | "imperial";
}

const SensorTreePanel: FunctionComponent<SensorTreePanelProps> = ({
    onSensorsSelect, onSensorGroupClick, onSensorTreeChange, onSensorCreateClick, rootSensorGroup, latestSensorTrends, tabletMode,
    onSensorEditClick, selectedNodes, unitsMode }) => {

    const [isSensorUploadModalShown, setIsSensorUploadModalShown] = React.useState(false);
    const [isAddModalShown, setIsAddModalShown] = React.useState(false);
    const [isEditModalShown, setIsEditModalShown] = React.useState(false);
    const [flatViewMode, setFlatViewMode] = React.useState(false);
    const [sensorGroupToBeEdited, setSensorGroupToBeEdited] = React.useState<SensorGroup | null>(null);
    const [searchTerm, setSearchTerm] = React.useState("");
    const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("asc");
    const [areaMoveWarning, setAreaMoveWarning] = React.useState<string | null>(null);
    const [treeMoveError, setTreeMoveError] = React.useState<string | null>(null);

    const showHighestButton = () => {
        return <div style={{ width: 85, display: "inline-block" }} className="Hello"><DropDownListControlled
            isSensorGroups={true}
            options={["Groups", "Highest"]}
            labelFor={l => l}
            selectedOption={flatViewMode ? "Highest" : "Groups"}
            onChange={newOption => setFlatViewMode(newOption === "Highest")}
        /></div>;
    };

    const addGroupButton = () => {
        // using the native material ui button rather than our own because it displays inline

        if (!canCreateSensorGroup()) {
            return null;
        }

        return <Button
            onClick={() => setIsAddModalShown(true)}
            variant="contained"
            startIcon={<MaterialUiIcons.Add />}
            sx={{
                "&:hover": {
                    background: "#F9EDE7",
                    color: "#ec6f41",
                },
                backgroundColor: "#ec6f41",
                color: "#fff",
                border: "1px solid #ec6f41",
                padding: "0px 6px",
                lineHeight: "23px"
            }}
            style={{ textTransform: "none", marginLeft: "18px", marginRight: "10px" }}
        >group</Button>;
    };

    const addSensorButton = () => {
        // using the native material ui button rather than our own because it displays inline

        if (Services.userHasPolicy("CreateSensorUi")) {
            return <Button
                onClick={() => onSensorCreateClick(creationAreaName)}
                variant="contained"
                startIcon={<MaterialUiIcons.Add />}
                sx={{
                    "&:hover": {
                        background: "#F9EDE7",
                        color: "#ec6f41",
                    },
                    backgroundColor: "#ec6f41",
                    color: "#fff",
                    border: "1px solid #ec6f41",
                    padding: "0px 6px",
                    lineHeight: "23px"
                }}
                style={{ textTransform: "none", marginLeft: "18px" }}
            >sensor</Button>;
        } else {
            return null;
        }
    };

    const uploadSensorsButton = () => {
        if (Services.userHasPolicy("CreateSensorUi")) {
            return <CloudUploadButton
                onClick={() => setIsSensorUploadModalShown(true)}
                title="Upload list of sensors"
                color="primary"
            />;
        } else {
            return null;
        }
    };

    const onSensorGroupEditButtonClicked = (sensorGroup: SensorGroup) => {
        setIsEditModalShown(true);
        setSensorGroupToBeEdited(sensorGroup);
    };

    const allSensors = new Map<string, Sensor>();
    const allSensorGroups = new Map<string, SensorGroup>();
    // These maps describe the tree that iDART1 shows. They are intentionally kept
    // separate from the API model's ParentGroupId, which is the real database link
    // and is never changed merely to arrange this legacy display.
    const visibleParentGroupIds = new Map<string, string>();
    const visibleParentSensorGroupIds = new Map<string, string>();
    // A node's Area is inferred from the real Area root that contains it. This
    // lets iDART1 allow ordinary reorganising within an Area, while still
    // refusing a move that would cross into another Area.
    const sensorGroupAreaRootIds = new Map<string, string>();
    const sensorAreaRootIds = new Map<string, string>();
    const readOnlyNodeIds = new Set<string>();
    const debouncedSearchTerm = useDebounce<string>(searchTerm, 500);
    const debouncedSearchTermSearches = debouncedSearchTerm.split(",");

    const sortMultiplier = sortOrder === "asc" ? 1 : -1;

    // The Area-aware endpoint preserves its real SensorGroup root so that iDART1
    // can keep using the database ID for its existing create workflows. This map
    // tells the display which of those roots are Area headings.
    const areaRootNames = (rootSensorGroup as AreaAwareSensorGroup).idartAreaRootNames || {};
    const areaRootName = (sensorGroup: SensorGroup) => sensorGroup.id ? areaRootNames[sensorGroup.id] : undefined;
    const isAreaRoot = (sensorGroup: SensorGroup) => areaRootName(sensorGroup) !== undefined;
    const creationAreaName = rootSensorGroup.id ? areaRootNames[rootSensorGroup.id] : undefined;

    // iDART1's existing create controls target this real root group. Label it in
    // the tree so users can immediately see which Area receives new content.
    const areaHeadingText = (sensorGroup: SensorGroup) => {
        const areaName = areaRootName(sensorGroup);
        if (!areaName) return sensorGroup.name || "Unknown group";

        return sensorGroup.id === rootSensorGroup.id
            ? `${areaName} (Default Area)`
            : areaName;
    };

    const isUnassignedRoot = (sensorGroup: SensorGroup) =>
        sensorGroup.id === rootSensorGroup.id && !isAreaRoot(sensorGroup);

    // Builds lookup tables from the nested API response without changing the model.
    // The original legacy root represents unassigned groups only when it is not
    // included in the Area metadata. Every mapped root is a real Area and is
    // displayed first, using its Area name from the API response.
    //
    // The virtual parent is client-only: it lets Area headings and unassigned content
    // be visual siblings without changing any persisted ParentGroupId values.
    function addSensorsToLookup(
        sensorGroup: SensorGroup,
        visibleParentGroupId?: string,
        inheritedReadOnly = false,
        inheritedAreaRootId?: string
    ) {
        const isUnassignedRootGroup = isUnassignedRoot(sensorGroup);
        const isAreaHeading = isAreaRoot(sensorGroup);
        const areaRootId = isAreaHeading ? sensorGroup.id : inheritedAreaRootId;
        // Only flatten direct children when the root is genuinely unassigned.
        // When the root itself is an Area (for example GTL), its children must
        // remain beneath that Area heading.
        const isDirectChildOfUnassignedRoot =
            visibleParentGroupId === rootSensorGroup.id && isUnassignedRoot(rootSensorGroup);
        const visualParentGroupId = isAreaHeading || isUnassignedRootGroup || isDirectChildOfUnassignedRoot
            ? legacyAreasTreeRootId
            : visibleParentGroupId || "";

        allSensorGroups.set(sensorGroup.id, sensorGroup);
        visibleParentGroupIds.set(sensorGroup.id, visualParentGroupId);
        if (areaRootId) sensorGroupAreaRootIds.set(sensorGroup.id, areaRootId);

        // The Area heading is display-only. Its actual descendants retain the
        // normal iDART1 controls, provided they are moved within the same Area.
        const isPromotedFromHiddenArea = !isUnassignedRootGroup
            && !isAreaHeading
            && !isDirectChildOfUnassignedRoot
            && visibleParentGroupId !== undefined
            && sensorGroup.parentGroupId !== visibleParentGroupId;
        // An Area heading does not make its contents read-only. Only a malformed
        // virtual branch remains protected, because its displayed parent does not
        // match the persisted relationship we would otherwise update.
        const branchIsReadOnly = inheritedReadOnly || isPromotedFromHiddenArea;
        const isReadOnly = branchIsReadOnly || isAreaHeading;

        // Area headings themselves are never editable SensorGroups. Their real
        // contents remain editable and movable within that Area.
        if (isReadOnly) {
            readOnlyNodeIds.add(sensorGroup.id);
        }

        sensorGroup.sensors
            ?.sort((a, b) => (a.description || "").localeCompare((b.description || "")) * sortMultiplier)
            .forEach(sensor => {
                const sensorId = sensor.id || "";
                allSensors.set(sensorId, sensor);
                if (areaRootId) sensorAreaRootIds.set(sensorId, areaRootId);
                visibleParentSensorGroupIds.set(
                    sensorId,
                    isUnassignedRootGroup ? legacyAreasTreeRootId : sensorGroup.id
                );

                // The Area heading itself is display-only, but direct sensors beneath
                // it are normal iDART1 items and must still be draggable/editable.
                if (branchIsReadOnly || (!isUnassignedRootGroup && sensor.parentGroupId !== sensorGroup.id)) {
                    readOnlyNodeIds.add(sensorId);
                }
            });

        sensorGroup.children
            ?.sort((a, b) => (a.name || "").localeCompare((b.name || "")) * sortMultiplier)
            .forEach(group => addSensorsToLookup(group, sensorGroup.id, branchIsReadOnly, areaRootId));
    }

    addSensorsToLookup(rootSensorGroup);

    React.useEffect(() => {
        allSensorGroups.clear();
        allSensors.clear();
        visibleParentGroupIds.clear();
        visibleParentSensorGroupIds.clear();
        sensorGroupAreaRootIds.clear();
        sensorAreaRootIds.clear();
        readOnlyNodeIds.clear();
        addSensorsToLookup(rootSensorGroup);
    }, [rootSensorGroup]);

    const toggleSortOrder = () => {
        setSortOrder((currentSortOrder) => currentSortOrder === "asc" ? "desc" : "asc");
    };

    // Put real Area headings first. The legacy root itself is a data container only;
    // its direct groups and sensors follow below with no artificial heading.
    const getVisibleGroupNodes = (groups: Iterable<[string, SensorGroup]>) => {
        const visibleGroups = Array.from(groups)
            .filter(([, sensorGroup]) => !isUnassignedRoot(sensorGroup));

        return [
            ...visibleGroups.filter(([, sensorGroup]) => isAreaRoot(sensorGroup)),
            ...visibleGroups.filter(([, sensorGroup]) => !isAreaRoot(sensorGroup))
        ].map(([groupId, sensorGroup]) => ({
            id: groupId,
            parent: visibleParentGroupIds.get(groupId) || "",
            text: areaHeadingText(sensorGroup),
            droppable: true
        }));
    };

    const hasAreas = Object.keys(areaRootNames).length > 0;

    const getSensorGroupHierarchyText = (sensorGroup: Sensorgroup): string => {
        const parentGroupId = visibleParentGroupIds.get(sensorGroup.id || "");
        const parentGroup = parentGroupId ? allSensorGroups.get(parentGroupId) : undefined;

        const displayName = areaRootName(sensorGroup) || sensorGroup.name || "";
        return parentGroup
            ? displayName + "," + getSensorGroupHierarchyText(parentGroup)
            : displayName;
    }

    const getSensorHierarchyText = (sensor: Sensor): string => {
        const visibleParentGroup = allSensorGroups.get(visibleParentSensorGroupIds.get(sensor.id || "") || "");
        return (sensor.description || "") + "," + (sensor.rfid || "") + "," + (visibleParentGroup ? getSensorGroupHierarchyText(visibleParentGroup) : "");
    }

    // Gets the text to search for in a group, including the names of the child sensors and groups, so that when you search for a child the parent groups show up
    const getGroupText = (group: Sensorgroup) : string => {
        return getSensorGroupHierarchyText(group) + ","  + (group.children?.map(getGroupText).join(",") || "") + "," + (group.sensors?.map(s => s.description + "," + s.rfid).join(",") || "");
    }

    // Filter and sort logic
    const filteredGroups = React.useMemo(() => {
        const filtered = Array.from(allSensorGroups).filter(([, group]) =>
            group && typeof group.name === "string" && debouncedSearchTermSearches.filter(search => getGroupText(group).toLowerCase().includes(search.toLowerCase())).length > 0
        );

        return new Map(filtered);
    }, [allSensorGroups, debouncedSearchTerm, sortOrder]);

    const filteredSensors = React.useMemo(() => {
        const matches = [];
        const groupsToExpand = new Set(); // To keep track of sensor groups to expand

        for (const sensor of allSensors.values()) {
            // Use optional chaining and provide a fallback empty string for the description
            const description = getSensorHierarchyText(sensor).toLowerCase() || "";
            if (debouncedSearchTermSearches.filter(search => description.includes(search.toLowerCase())).length > 0) {
                matches.push(sensor);
                const visibleParentGroupId = visibleParentSensorGroupIds.get(sensor.id || "");
                if (visibleParentGroupId) {
                    groupsToExpand.add(visibleParentGroupId); // Add the visible parent group ID to the set if it exists
                }
            }
        }

        return { matches, groupsToExpand };
    }, [allSensors, debouncedSearchTerm, searchTerm]);

    if (latestSensorTrends) {
        console.log("latestSensorTrends",
            latestSensorTrends
                .filter(t => t.parameters?.trendPeriodType === Trendperiodtype.ShortTerm)
                .sort((a, b) => (a.corrosionRate || 0) - (b.corrosionRate || 0))
                .filter(t => t.corrosionRate !== 0)
                .map(t => allSensors.get(t.sensorId!))
        );
    }

    const ref = React.useRef<TreeMethods>(null);
    // Keep the Area used by the iDART1 create buttons open on first load, so
    // existing root-level sensors are immediately visible to the user.
    const initiallyOpenGroupIds = React.useMemo(
        () => creationAreaName && rootSensorGroup.id ? [rootSensorGroup.id] : [],
        [creationAreaName, rootSensorGroup.id]
    );

    const isMES = (node?: NodeModel) => {
        if (node === undefined) return false;

        // droppable means a sensor group
        if (node.droppable) {
            const sensorGroup = allSensorGroups.get(node.id as string);
            if (sensorGroup === undefined) return false;

            return sensorGroup.name?.includes("MES");
        } else {
            const sensor = allSensors.get(node.id as string);
            if (sensor === undefined) return false;

            const sensorGroup = allSensorGroups.get(visibleParentSensorGroupIds.get(sensor.id || "") || "");
            if (sensorGroup === undefined) return false;

            return sensorGroup.name?.includes("MES");
        }
    };

    const isMESNode = (node?: NodeModel) => {
        if (node === undefined) return false;

        // droppable means a sensor group
        if (node.droppable) {
            return false;
        } else {
            const sensor = allSensors.get(node.id as string);
            if (sensor === undefined) return false;

            const sensorGroup = allSensorGroups.get(visibleParentSensorGroupIds.get(sensor.id || "") || "");
            if (sensorGroup === undefined) return false;

            return sensorGroup.name?.includes("MES");
        }
    }

    const isReadOnlyNode = (node?: NodeModel) =>
        node !== undefined && readOnlyNodeIds.has(node.id as string);

    const isReadOnlyNodeId = (nodeId?: string | number) =>
        nodeId !== undefined && readOnlyNodeIds.has(nodeId.toString());

    const isReadOnlySelectedSensor = (node?: NodeModel) =>
        node !== undefined
            && !node.droppable
            && selectedNodes.includes(node.id as string)
            && selectedNodes.some(sensorId => readOnlyNodeIds.has(sensorId));

    const isAreaHeadingNode = (node?: NodeModel) => {
        if (node === undefined || !node.droppable) return false;

        const sensorGroup = allSensorGroups.get(node.id as string);
        return sensorGroup !== undefined && isAreaRoot(sensorGroup);
    };

    const isAreaHeadingNodeId = (nodeId?: string | number) => {
        if (nodeId === undefined) return false;

        const sensorGroup = allSensorGroups.get(nodeId.toString());
        return sensorGroup !== undefined && isAreaRoot(sensorGroup);
    };

    const getAreaRootIdForNode = (nodeId?: string | number) => {
        const id = nodeId?.toString();
        return id ? sensorGroupAreaRootIds.get(id) || sensorAreaRootIds.get(id) : undefined;
    };

    const getAreaNameForNode = (nodeId?: string | number) => {
        const areaRootId = getAreaRootIdForNode(nodeId);
        return areaRootId ? areaRootNames[areaRootId] || "an Area" : "unassigned content";
    };

    const getCrossAreaMoveWarning = (
        dragSourceId?: string | number,
        dropTargetId?: string | number,
        dragSource?: NodeModel
    ) => {
        if (dragSourceId === undefined || dropTargetId === undefined) return undefined;

        // A selected sensor drag moves the entire selection. Check every selected
        // item so a mixed-Area selection cannot partially move by accident.
        const sourceIds = dragSource !== undefined
            && !dragSource.droppable
            && selectedNodes.includes(dragSource.id as string)
            ? selectedNodes
            : [dragSourceId.toString()];
        const destinationAreaRootId = getAreaRootIdForNode(dropTargetId);
        const sourceOutsideDestinationArea = sourceIds.find(sourceId =>
            getAreaRootIdForNode(sourceId) !== destinationAreaRootId);

        return sourceOutsideDestinationArea
            ? `Moving between Areas is not available in iDART1 (${getAreaNameForNode(sourceOutsideDestinationArea)} to ${getAreaNameForNode(dropTargetId)}).`
            : undefined;
    };

    const runTreeMove = async (move: () => Promise<void>) => {
        try {
            // Do not use Services.batch here. Its error path assumes a global UI
            // blocking callback that this screen does not provide, which masked
            // the real result of a sensor move.
            await move();
            await onSensorTreeChange();
        } catch (error) {
            console.error("Unable to move item in the sensor tree", error);
            const apiErrorMessage = error instanceof ServiceGeneralError
                ? error.responseErrors?.map(responseError => responseError.message).join(" ")
                : undefined;
            setTreeMoveError(apiErrorMessage || "The item could not be moved. Please refresh and try again.");
        }
    };

    return (
        <ReactDnd.DndProvider backend={HTML5Backend}>
            <div style={{ width: "100%", height: "100%", overflowY: "auto", backgroundColor: "white" }}>
                <SimplePanel
                    isNewStyleTitle={true}
                    shouldApplyPadding={false}
                    title={hasAreas ? "Areas & Sensor Groups" : "Sensor Groups"}
                    titleAlignment="left"
                    size="small"
                    actionButton={
                        <>
                            {showHighestButton()}
                            {addSensorButton()}
                            {addGroupButton()}
                            {uploadSensorsButton()}
                        </>
                    }
                    searchRow={
                        <>
                            <div style={{ display: "flex", gap: "10px", alignItems: "center",justifyContent: "center",padding: "6px 0px"  }}>
                                <FormControl style={{ width:"80%" }}>
                                    <OutlinedInput 
                                        placeholder="Search for Sensor Groups or Sensors" 
                                        type={"search"}
                                        color="primary" 
                                        sx={{
                                            height:"34px", 
                                            marginRight:"20px",
                                            "& .MuiOutlinedInput-notchedOutline":{
                                                borderColor:" #ec6f41",
                                            },
                                        }} 
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            if (ref.current) {
                                                if (e.target.value !== "") {
                                                    ref.current.openAll();
                                                } else {
                                                    ref.current.closeAll();
                                                }
                                            }
                                        }}
                                        title="Separate different search terms by comma"
                                        style={{ fontSize:"14px", border:"1px solid #EC6F41",marginRight:"5px" }}
                                    />
                                </FormControl>
                                <SortButton sortOrder={sortOrder} onToggle={toggleSortOrder}/>
                            </div>
                        </>
                    }
                    content={
                        <Tree
                            ref={ref}
                            tree={
                                flatViewMode ?
                                    [
                                        { "id": rootSensorGroup.id, parent: "", "text": "Root", droppable: true },
                                        ...(latestSensorTrends !== undefined ? latestSensorTrends
                                            .filter(t => t.parameters?.trendPeriodType === Trendperiodtype.ShortTerm)
                                            .sort((a, b) => (a.corrosionRate || 0) - (b.corrosionRate || 0))
                                            .filter(t => t.corrosionRate !== 0)
                                            .map(t => {
                                                const sensor = allSensors.get(t.sensorId!);
                                                return {
                                                    "id": sensor?.id || "",
                                                    "parent": rootSensorGroup.id,
                                                    "text": sensor?.description || "Unknown sensor",
                                                    "droppable": false
                                                };
                                            }) : [])
                                    ] :
                                    // This section is adjusted to conditionally render based on searchTerm
                                    searchTerm === "" ?
                                        [
                                            // Original behavior: display all groups and sensors
                                            ...getVisibleGroupNodes(allSensorGroups),
                                            ...Array.from(allSensors).map(([sensorId, sensor]) => ({
                                                "id": sensorId,
                                                "parent": visibleParentSensorGroupIds.get(sensorId) || legacyAreasTreeRootId,
                                                "text": sensor.description || "Unknown Sensor"
                                            }))
                                        ] :
                                        // When there is a searchTerm: filter
                                        [
                                            ...getVisibleGroupNodes(filteredGroups),
                                            ...filteredSensors.matches.map(sensor => ({
                                                id: sensor.id,
                                                parent: visibleParentSensorGroupIds.get(sensor.id || "") || legacyAreasTreeRootId,
                                                text: sensor.description || "Unknown Sensor",
                                                droppable: false
                                            }))
                                        ]
                            }
                            classes={{
                                "container": "sensorListUl"
                            }}
                            rootId={flatViewMode ? rootSensorGroup.id : legacyAreasTreeRootId}
                            initialOpen={initiallyOpenGroupIds}
                            sort={false}
                            insertDroppableFirst={true}
                            onDrop={async (_newTree, { dragSourceId, dropTargetId, dragSource }) => {
                                if (!Services.userCanEdit()) return;

                                const crossAreaMoveWarning = getCrossAreaMoveWarning(dragSourceId, dropTargetId, dragSource);
                                if (crossAreaMoveWarning) {
                                    setAreaMoveWarning(crossAreaMoveWarning);
                                    return;
                                }

                                // The virtual Area parent is only used for rendering. A drop
                                // there would have no corresponding SensorGroup in the API.
                                if (dropTargetId === legacyAreasTreeRootId
                                    || isReadOnlyNode(dragSource)
                                    || (isReadOnlyNodeId(dropTargetId) && !isAreaHeadingNodeId(dropTargetId))) {
                                    return;
                                }

                                if (dragSource?.droppable) {
                                    const source = allSensorGroups.get(dragSourceId as string);
                                    if (!source || source.parentGroupId === dropTargetId as string) return;
                                    await runTreeMove(async () => {
                                        await Services.SensorGroups.moveSensorGroup(source, dropTargetId as string);
                                    });
                                } else {
                                    // If item being dragged is one of a group that is selected, move the whole group
                                    if (dragSourceId && selectedNodes.includes(dragSourceId.toString())) {
                                        await runTreeMove(async () => {
                                            for (const node of selectedNodes) {
                                                const source = allSensors.get(node);
                                                if (!source || readOnlyNodeIds.has(node) || source.parentGroupId === dropTargetId) continue;

                                                await Services.Sensors.moveSensor(source, dropTargetId as string);
                                            }
                                        });
                                    } else {
                                        const source = allSensors.get(dragSourceId as string);
                                        if (!source || source.parentGroupId === dropTargetId) return;
                                        await runTreeMove(async () => {
                                            await Services.Sensors.moveSensor(source, dropTargetId as string);
                                        });
                                    }
                                }
                            }}
                            canDrag={(node) =>
                                Services.userCanEdit()
                                && !isMESNode(node)
                                && !isAreaHeadingNode(node)
                                && !isReadOnlyNode(node)
                                && !isReadOnlySelectedSensor(node)
                            }
                            canDrop={(_tree, { dragSource, dropTarget }) =>
                                !Services.userCanEdit()
                                    || isMES(dropTarget)
                                    || isReadOnlyNode(dragSource)
                                    || (isReadOnlyNode(dropTarget) && !isAreaHeadingNode(dropTarget))
                                    ? false
                                    : undefined
                            }
                            render={(node, { depth, isOpen, onToggle }) => {

                                const longTermTrend = FirstIfNotEmpty(latestSensorTrends?.filter(t => t.sensorId === node.id && t.parameters?.trendPeriodType === Trendperiodtype.LongTerm)) || undefined;
                                const shortTermTrend = FirstIfNotEmpty(latestSensorTrends?.filter(t => t.sensorId === node.id && t.parameters?.trendPeriodType === Trendperiodtype.ShortTerm)) || undefined;
                                const statsLoaded = latestSensorTrends !== undefined;

                                if (node.droppable) {
                                    const sensorGroup = allSensorGroups.get(node.id as string)!;
                                    const isReadOnly = readOnlyNodeIds.has(sensorGroup.id);
                                    const isAreaHeading = isAreaRoot(sensorGroup);
                                    
                                    return <SensorNode
                                        label={node.text}
                                        depth={depth}
                                        droppable={true}
                                        isOpen={isOpen}
                                        isAreaHeading={isAreaHeading}
                                        isDefaultArea={isAreaHeading && sensorGroup.id === rootSensorGroup.id}
                                        onClick={() => isAreaHeading ? undefined : onSensorGroupClick(sensorGroup)}
                                        onContextMenu={canEditSensorGroup(isReadOnly) ? () => onSensorGroupEditButtonClicked(sensorGroup) : undefined}
                                        onToggle={onToggle}
                                        selected={!isAreaHeading && selectedNodes.includes(sensorGroup.id)}
                                        tabletMode={tabletMode}
                                        status="normal"
                                        onDelete={canDeleteSensorGroup(isReadOnly, sensorGroup.children?.length === 0 && sensorGroup.sensors?.length === 0) ?
                                            async () => {
                                                await Services.batch(async () => {
                                                    await Services.SensorGroups.deleteSensorGroup(sensorGroup.id);
                                                    await onSensorTreeChange();
                                                }, true)
                                            }
                                            : undefined
                                        }
                                    />;
                                } else {
                                    const sensor = allSensors.get(node.id as string)!;
                                    const isReadOnly = readOnlyNodeIds.has(sensor.id);
                                    const type = sensor.sensorType.productName;

                                    const getThicknessStatus = (thickness: number | undefined) => {
                                        if (thickness === undefined) return "normal";

                                        if (sensor.warningParameters.criticalThicknessInMetres && thickness < sensor.warningParameters.criticalThicknessInMetres) {
                                            return "error";
                                        } else if (sensor.warningParameters.warningThicknessInMetres && thickness < sensor.warningParameters.warningThicknessInMetres) {
                                            return "warning";
                                        } else {
                                            return "normal";
                                        }
                                    }

                                    return <SensorNode
                                        type={type}
                                        label={node.text}
                                        depth={depth}
                                        droppable={false}
                                        isOpen={isOpen}
                                        onClick={(ctrlKey, shiftKey) => {
                                            if (shiftKey) {
                                                // Algorithm for shift select:
                                                // Get the lowest and highest indices of the sensors in both the existing sensor selection and the additional sensor
                                                // and find all sensors between them

                                                // Using .get! is a necessary evil, the ids should all exist in allSensors so should be okay to do
                                                const allSensorValues = [...allSensors.values()];
                                                const allSensorIds = allSensorValues.map(s => s.id);

                                                const selectedIndices = selectedNodes.map(node => allSensorIds.findIndex(id => id === node));
                                                const sensorIndex = allSensorIds.findIndex(id => id === sensor.id);

                                                const combinedIndices = [...selectedIndices, sensorIndex];
                                                const minIndex = Math.min(...combinedIndices);
                                                const maxIndex = Math.max(...combinedIndices);

                                                const newSelectedSensorIds = allSensorIds.filter((_id, index) => index >= minIndex && index <= maxIndex);
                                                const newSelectedSensors = newSelectedSensorIds.map(id => allSensors.get(id)!);

                                                onSensorsSelect(newSelectedSensors);
                                            } else { 
                                                onSensorsSelect(ctrlKey ? [...selectedNodes.map(n => allSensors.get(n)!), sensor] : [sensor]);
                                            }
                                        }}
                                        onContextMenu={isReadOnly ? undefined : () => onSensorEditClick(sensor)}
                                        onToggle={onToggle}
                                        selected={selectedNodes.includes(sensor.id)}
                                        tabletMode={tabletMode}
                                        status={getThicknessStatus(longTermTrend?.latestThickness)}
                                        corrosionRates={
                                            (!node.droppable && statsLoaded) ?
                                                <>[
                                                    {
                                                        longTermTrend?.latestThickness ?
                                                            <DisplacementText
                                                                displacementInMetres={longTermTrend?.latestThickness}
                                                                unitsMode={unitsMode}
                                                            />
                                                            : "N/A"
                                                    },&nbsp;
                                                    {
                                                        shortTermTrend?.corrosionRate ?
                                                            <CorrosionRateText
                                                                corrosionRateInMMY={shortTermTrend?.corrosionRate * 1000}
                                                                unitsMode={unitsMode}
                                                                noCorrosionText="0"
                                                                tooltip="Short-term corrosion rate"
                                                            />
                                                            : "N/A"
                                                    },&nbsp;
                                                    {
                                                        longTermTrend?.corrosionRate ?
                                                            <CorrosionRateText
                                                                corrosionRateInMMY={longTermTrend?.corrosionRate * 1000}
                                                                unitsMode={unitsMode}
                                                                noCorrosionText="0"
                                                                tooltip="Long-term corrosion rate"
                                                            />
                                                            : "N/A"
                                                    }
                                                    ]</>
                                                : null
                                        }
                                    />;
                                }
                            }}
                        />
                    }
                />

                <Snackbar
                    open={areaMoveWarning !== null}
                    autoHideDuration={6000}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                    onClose={() => setAreaMoveWarning(null)}
                >
                    <Alert severity="warning" onClose={() => setAreaMoveWarning(null)}>
                        {areaMoveWarning}
                    </Alert>
                </Snackbar>

                <Snackbar
                    open={treeMoveError !== null}
                    autoHideDuration={6000}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                    onClose={() => setTreeMoveError(null)}
                >
                    <Alert severity="error" onClose={() => setTreeMoveError(null)}>
                        {treeMoveError}
                    </Alert>
                </Snackbar>

            </div>
            <CreateSensorGroupModalPanel
                parentGroupId={rootSensorGroup.id}
                creationAreaName={creationAreaName}
                shouldBeShown={isAddModalShown}
                onClose={() => setIsAddModalShown(false)}
                onCreate={async () => {
                    await onSensorTreeChange();
                    setIsAddModalShown(false);
                }}
            />
            <UploadSensorsModalPanel
                parentGroupId={rootSensorGroup.id}
                shouldBeShown={isSensorUploadModalShown}
                onClose={async () => {
                    setIsSensorUploadModalShown(false);
                    await onSensorTreeChange();
                }}
            />
            {sensorGroupToBeEdited && <EditSensorGroupModalPanel
                sensorGroup={sensorGroupToBeEdited}
                shouldBeShown={isEditModalShown}
                onClose={() => setIsEditModalShown(false)}
                onSave={async () => {
                    await onSensorTreeChange();
                    setIsEditModalShown(false);
                }}
            />}
        </ReactDnd.DndProvider>
    );
}

interface SensorDeleteButtonProps {
    tabletMode: boolean;
    onClick(): void;
}

const SensorDeleteButton: FunctionComponent<SensorDeleteButtonProps> = ({ tabletMode, onClick }) => {
    if (tabletMode) {
        return <DeleteIconButton
            onClick={event => {
                event?.stopPropagation();
                onClick();
            }}
        />;
    } else {
        return <IconButton
            size="small"
            icon={
                <CloseIcon
                    fontSize="small"
                    htmlColor="black"
                />
            }
            onClick={ev => {
                if (ev) {
                    ev.stopPropagation();
                    ev.preventDefault();
                }
                onClick();
            }}
        />;
    }
}

export default SensorTreePanel;

interface SensorNodeProps {
    type?: string;
    label: string;
    tabletMode: boolean;
    selected: boolean;
    droppable: boolean;
    isOpen: boolean;
    depth: number;
    onClick: (ctrlKey: boolean, shiftKey: boolean) => void;
    onContextMenu?: () => void;
    onToggle: () => void;
    onDelete?: () => void;
    corrosionRates?: React.ReactNode;
    status: "normal" | "warning" | "error";
    isAreaHeading?: boolean;
    isDefaultArea?: boolean;
}

const SensorNode: FunctionComponent<SensorNodeProps> = ({ type, label, depth, onClick, onContextMenu, onToggle, selected, droppable, isOpen, tabletMode, onDelete, corrosionRates, status, isAreaHeading, isDefaultArea }) => {

    let style = tabletMode ? sensorLabelStyle : sensorLabelCompactStyle;

    if (status === "error") {
        style = merge(style, { color: alertErrorForegroundRed });
    } else if (status === "warning") {
        style = merge(style, { color: alertWarningForegroundAmber });
    }

    if (isAreaHeading) {
        style = merge(style, { color: "#ec6f41", fontWeight: "bold" });
    }

    return <div
        style={{
            marginLeft: depth * 25,
            fontSize: "9pt",
            padding: 4,
            backgroundColor: selected ? "lightblue" : undefined,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            ...style
        }}
        onClick={(ev) => {
            onClick(ev.ctrlKey, ev.shiftKey);
        }}
        onContextMenu={onContextMenu ? ev => {
            ev.preventDefault();
            onContextMenu();
        } : undefined}
        onDoubleClick={onContextMenu ? ev => {
            ev.stopPropagation();
            ev.preventDefault();
            onContextMenu();
        } : undefined}
    >
        <span onClick={ev => {
            ev.stopPropagation();
            onToggle();
        }}>
            {droppable && (
                <span style={{ width: 50 }}>
                    {
                        isOpen ?
                            <ArrowDropDownIcon fontSize="small" sx={{ alignItems: "center" }} /> :
                            <ArrowRightIcon fontSize="small" sx={{ alignItems: "center" }} />
                    }&nbsp;
                </span>
            )}
        </span>
        {droppable && label.includes("MES") && <MesSvg style={{ marginRight: 10 }} />}
        {!droppable && (type === "DL1" ? <SdlSvg style={{ marginRight: 5, height: "12px" }} /> : <SensorSvg style={{ marginRight: "5px" }} />)}
        <span>
            {label}&nbsp;
            {isDefaultArea && <Tooltip
                title={`New sensors and groups are created in: ${label.replace(" (Default Area)", "")}`}
                arrow
            >
                <span
                    aria-label="Default Area information"
                    onClick={event => event.stopPropagation()}
                    style={{ display: "inline-flex", verticalAlign: "middle", cursor: "help", marginRight: 4 }}
                    tabIndex={0}
                >
                    <MaterialUiIcons.InfoOutlined sx={{ fontSize: "15px" }} />
                </span>
            </Tooltip>}
            {
                onDelete ? <SensorDeleteButton
                    onClick={onDelete}
                    tabletMode={tabletMode}
                /> : null
            }&nbsp;
            {corrosionRates}
        </span>
    </div>;
}
